import { useState } from 'react'
import { Outlet } from 'react-router-dom'
import { clsx } from 'clsx'
import { Sidebar } from './Sidebar'
import { TopBar } from './TopBar'

function isDesktop(): boolean {
  if (typeof window === 'undefined') return true
  return window.matchMedia('(min-width: 768px)').matches
}

const COLLAPSED_STORAGE_KEY = 'nexo-sidebar-collapsed'

/** Sin preferencia guardada (primera vez en este navegador) arranca expandido, como se
 * pidio explicitamente. Despues de eso, recuerda el ultimo modo elegido entre recargas. */
function getStoredCollapsed(): boolean {
  if (typeof window === 'undefined') return false
  try {
    return window.localStorage.getItem(COLLAPSED_STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

export function AppLayout() {
  // Movil: el sidebar es un cajon que se muestra/oculta por completo (con fondo oscuro),
  // cerrado por defecto. Escritorio: el sidebar SIEMPRE esta visible -- el boton de
  // hamburguesa solo alterna entre expandido (con etiquetas) y una mini-barra de solo
  // iconos, nunca lo oculta; el modo elegido se recuerda entre recargas (localStorage).
  const [mobileOpen, setMobileOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(getStoredCollapsed)

  function handleMenuClick() {
    if (isDesktop()) {
      setCollapsed((v) => {
        const next = !v
        try {
          window.localStorage.setItem(COLLAPSED_STORAGE_KEY, next ? '1' : '0')
        } catch {
          // localStorage no disponible (modo privado, etc.) -- el toggle sigue funcionando
          // en memoria, solo no persiste entre recargas.
        }
        return next
      })
    } else {
      setMobileOpen((v) => !v)
    }
  }

  return (
    <div className="flex min-h-screen">
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-32 -left-32 h-96 w-96 animate-[aurora-drift_24s_ease-in-out_infinite] rounded-full bg-accent/15 blur-[120px]" />
        <div className="absolute top-1/3 -right-24 h-80 w-80 animate-[aurora-drift_28s_ease-in-out_infinite_reverse] rounded-full bg-chart-purple/15 blur-[120px]" />
        <div className="absolute bottom-0 left-1/3 h-72 w-72 animate-[aurora-drift_32s_ease-in-out_infinite] rounded-full bg-chart-teal/10 blur-[120px]" />
      </div>
      {mobileOpen && (
        <button
          className="fixed inset-0 z-30 bg-black/50 md:hidden"
          aria-label="Cerrar menú"
          onClick={() => setMobileOpen(false)}
        />
      )}
      <Sidebar mobileOpen={mobileOpen} collapsed={collapsed} onNavigate={() => setMobileOpen(false)} />
      <div
        className={clsx(
          'flex min-w-0 flex-1 flex-col transition-[margin] duration-200',
          collapsed ? 'md:ml-[4.5rem]' : 'md:ml-60',
        )}
      >
        <TopBar onMenuClick={handleMenuClick} expanded={isDesktop() ? !collapsed : mobileOpen} />
        <main className="flex-1 p-6 max-md:p-4">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
